/*
 * Copyright © 2018-2024, GlobalVET AB
 *
 * All rights reserved. No part or the whole of this source code and the compiled program
 * may be reproduced, copied, distributed, disseminated to the public, adapted or transmitted
 * in any form or by any means, including photocopying, recording, or other electronic or
 * mechanical methods, without the prior written permission of GlobalVET AB. This source code
 * and the compiled program may only be used for the purposes of GlobalVET AB. This source code
 * and the compiled program shall be kept confidential and shall not be made public or made
 * available or disclosed to any unauthorized person. Any dispute or claim arising out of the
 * breach of these provisions shall be governed by and construed in accordance with the
 * laws of Sweden.
 */

import {
  FieldError,
  FieldErrorsImpl,
  Merge,
  RegisterOptions,
} from "react-hook-form";
import React, { ReactElement, useEffect, useState } from "react";
import { Tooltip } from "../../../Tooltip";
import { Colors } from "../../../../models/Colors";
import {
  getStringFromEnumsOrReturn,
  strings,
} from "../../../../common/Strings/Strings";
import { AutoCompleteOptions } from "../../../../models/AutoCompleteOptions";
import { Option } from "../../../../models/Option";
import { RegisterReturnType } from "../../../../models/RegisterReturnType";
import { InfoFilled } from "../../../../common/Icons/InfoFilled";

interface FieldProps {
  name: string;
  options: Option[];
  register(ref: string, registerOptions?: RegisterOptions): RegisterReturnType;
  autoComplete?: AutoCompleteOptions;
  classLabel?: string;
  className?: string;
  defaultValue?: any;
  disabledChooseOptionTitle?: string;
  error?: FieldError | Merge<FieldError, FieldErrorsImpl<any>>;
  fieldOptions?: RegisterOptions;
  firstOption?: Option;
  isChooseOption?: boolean;
  label?: string;
  labelIcon?: string;
  labelOff?: boolean;
  onChange?(e: React.ChangeEvent<HTMLSelectElement>): void;
  optional?: boolean;
  readOnly?: boolean;
  required?: boolean;
  showRequired?: boolean;
  separationEnabled?: boolean;
  toolTipText?: string | ReactElement;
  width?: string;
}

const Select = ({
  name,
  options,
  register,
  autoComplete = AutoCompleteOptions.on,
  disabledChooseOptionTitle,
  classLabel,
  className,
  defaultValue,
  error,
  fieldOptions,
  firstOption,
  isChooseOption = true,
  label,
  labelIcon,
  labelOff = false,
  onChange,
  optional = false,
  readOnly = false,
  required = false,
  separationEnabled = false,
  showRequired,
  toolTipText,
  width,
}: FieldProps): ReactElement => {
  const [registerOptions, setRegisterOptions] = useState<RegisterOptions>(
    fieldOptions || {}
  );

  useEffect(() => {
    if (required) {
      setRegisterOptions({ ...registerOptions, required: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [required]);

  const labelClass = classLabel || "";

  const tooltipStyle = {
    borderBottomLeftRadius: "0",
    borderTopLeftRadius: "0",
    fontSize: "20px",
    borderLeft: "0",
    opacity: readOnly ? "0.5" : "1",
  };

  const optionList = options.map((option: Option) => {
    const title = separationEnabled
      ? option.title
          .split(" ")
          .map((str: string) => getStringFromEnumsOrReturn(str))
          .join(" ")
      : getStringFromEnumsOrReturn(option.title);

    return (
      <option value={option.value} key={option.value}>
        {title}
      </option>
    );
  });

  return (
    <div
      className={`${className} ${error ? "has-error" : ""}`}
      style={{ width }}
    >
      {labelOff ? (
        <></>
      ) : (
        <label className={labelClass} htmlFor={name}>
          {labelIcon ? <i className={labelIcon} /> : ""} {label}
          {showRequired ? " *" : ""}
          {optional ? (
            <span style={{ color: Colors.INPUTBORDER }}>
              {" "}
              ({strings.optional})
            </span>
          ) : (
            <></>
          )}
        </label>
      )}
      <div className="flex items-center">
        {register ? (
          <select
            {...register(name, registerOptions)}
            autoComplete={autoComplete || AutoCompleteOptions.on}
            className={`tw-input ${className}`}
            name={name}
            onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
              void register(name).onChange(e);
              if (onChange) {
                onChange(e);
              }
            }}
            disabled={readOnly}
            defaultValue={isChooseOption ? "" : defaultValue}
          >
            {isChooseOption ? (
              <option value="" disabled>
                {disabledChooseOptionTitle || strings.pleaseChoose}
              </option>
            ) : (
              <></>
            )}
            {firstOption ? (
              <option value={firstOption.value}>{firstOption.title}</option>
            ) : (
              <></>
            )}
            {optionList}
          </select>
        ) : (
          <></>
        )}
        {toolTipText ? (
          <div style={tooltipStyle}>
            <Tooltip content={toolTipText} placement="top">
              <span>
                <InfoFilled className="text-sky-500" />
              </span>
            </Tooltip>
          </div>
        ) : (
          <></>
        )}
      </div>
      <p>{error && error.message?.toString()}</p>
    </div>
  );
};

export default Select;
